// src/helpers/msalSession.js
//
// Centralizes MSAL account selection and session teardown so logout/login
// cannot resurrect a previous user's cached account or bearer token.

import msalInstance from "../msalInstance";
import { tryParseJwt } from "./token";

/**
 * Prefer the active MSAL account. If none is set, only auto-select when there
 * is exactly one cached account — never guess with getAllAccounts()[0] when
 * multiple accounts remain after an incomplete logout.
 */
export function getSessionMsalAccount(instance = msalInstance) {
  const active = instance.getActiveAccount();
  if (active) {
    return active;
  }

  const accounts = instance.getAllAccounts();
  if (!accounts || accounts.length === 0) {
    return null;
  }
  if (accounts.length === 1) {
    return accounts[0];
  }

  return null;
}

/**
 * Remove MSAL tokens/accounts from memory and localStorage. Safe to call during
 * logout before redirect; does not require IdP round-trip.
 */
export async function clearMsalClientSession(instance = msalInstance) {
  try {
    instance.setActiveAccount(null);
  } catch (_e) {
    // ignore
  }

  try {
    if (typeof instance.clearCache === "function") {
      await instance.clearCache();
    }
  } catch (_e) {
    // ignore — still strip localStorage below
  }

  try {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith("msal.")) {
        localStorage.removeItem(key);
      }
    });
  } catch (_e) {
    // ignore
  }
}

/**
 * Returns true when JWT claims plausibly belong to the MSAL account (or when
 * claims cannot be compared). Returns false when they clearly mismatch.
 */
export function accessTokenMatchesMsalAccount(accessToken, account) {
  if (!accessToken || !account) {
    return false;
  }

  const claims = tryParseJwt(accessToken);
  if (!claims) {
    return true;
  }

  const accountUsername = String(
    account.username || account.localAccountId || "",
  ).toLowerCase();
  if (!accountUsername) {
    return true;
  }

  const claimValues = [
    claims.preferred_username,
    claims.email,
    Array.isArray(claims.emails) ? claims.emails[0] : claims.emails,
    claims.sub,
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());

  if (claimValues.length === 0) {
    return true;
  }

  return claimValues.some(
    (claim) =>
      claim === accountUsername ||
      claim.includes(accountUsername) ||
      accountUsername.includes(claim),
  );
}
